import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { CircularProgress, Typography } from '@mui/material'

import { FlexDiv } from '../../components'
import { AppRoutes } from '../../helpers/Routes'
import { getShortFormDate } from '../../helpers/stringHelpers'
import { useInspectionHub } from '../../hooks/useInspectionHub'
import { RootState } from '../../store'
import { InspectionAreaResults } from './InspectionAreaResults'
import { InspectionDetailShell } from './InspectionDetailShell'
import {
    getEarliestDueDate,
    getLatestCompletedDate,
    countAreas,
} from './inspectionUtils'

export const MoveInInspectionDetail = () => {
    const navigate = useNavigate()
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const leaseId = selectedLease?.id
    const apartmentId = selectedLease?.apartment_id

    const { hubData, isLoading, getInspectionHub } = useInspectionHub(
        leaseId,
        apartmentId,
    )

    useEffect(() => {
        if (leaseId && apartmentId) {
            getInspectionHub()
        }
    }, [leaseId, apartmentId])

    const handleBack = () => navigate(AppRoutes.inspections)

    if (!leaseId || !apartmentId) {
        return (
            <InspectionDetailShell title="Move-In Inspection" onBack={handleBack}>
                <Typography color="textSecondary">
                    Select a lease to view inspection results.
                </Typography>
            </InspectionDetailShell>
        )
    }

    if (isLoading && !hubData) {
        return (
            <InspectionDetailShell title="Move-In Inspection" onBack={handleBack}>
                <FlexDiv style={{ justifyContent: 'center', padding: 32, width: '100%' }}>
                    <CircularProgress size={40} />
                </FlexDiv>
            </InspectionDetailShell>
        )
    }

    const moveIn = hubData?.move_in
    if (!moveIn || moveIn.state !== 'complete') {
        return (
            <InspectionDetailShell title="Move-In Inspection" onBack={handleBack}>
                <Typography color="textSecondary" sx={{ mb: 2 }}>
                    Move-in inspection results are not available yet.
                </Typography>
            </InspectionDetailShell>
        )
    }

    const dueDate = getEarliestDueDate(moveIn.detail)
    const completedDate = getLatestCompletedDate(moveIn.detail)
    const metadata: string[] = []
    if (dueDate) metadata.push(`Due ${getShortFormDate(dueDate)}`)
    if (completedDate) metadata.push(`Completed ${getShortFormDate(completedDate)}`)
    metadata.push(
        `${countAreas(moveIn.detail)} area${countAreas(moveIn.detail) === 1 ? '' : 's'} inspected`,
    )

    return (
        <InspectionDetailShell
            title="Move-In Inspection"
            status="complete"
            metadata={metadata}
            onBack={handleBack}
        >
            <InspectionAreaResults
                inspections={moveIn.detail}
                showVideos={false}
                defaultTab="issues"
                filterMode="tenant_issues"
            />
        </InspectionDetailShell>
    )
}
